import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Eye, MousePointer, Users, Clock, Globe, Smartphone, TrendingUp, LogOut, MessageSquare, Check, X, Trash2, RotateCcw, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { WorldMap } from '../components/WorldMap';
import '../components/adminExtras.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#00D4B4', '#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981'];

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('cxsage_admin_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [period, setPeriod] = useState('weekly');
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [feedback, setFeedback] = useState([]);
  
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated, period]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      if (response.data.success && response.data.token) {
        localStorage.setItem('cxsage_admin_token', response.data.token);
        setIsAuthenticated(true);
        toast.success('Login successful!');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchData = async () => {
    const token = localStorage.getItem('cxsage_admin_token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    try {
      const [statsRes, timelineRes, feedbackRes] = await Promise.all([
        axios.get(`${API}/admin/stats?period=${period}`, config),
        axios.get(`${API}/admin/timeline?period=${period}`, config),
        axios.get(`${API}/feedback`, config)
      ]);
      
      setStats(statsRes.data);
      setTimeline(timelineRes.data);
      setFeedback(feedbackRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        handleLogout();
      } else {
        console.error('Error fetching analytics:', error);
        toast.error('Error loading analytics data');
      }
    }
  };
  
  const handleLogout = () => {
    localStorage.removeItem('cxsage_admin_token');
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setStats(null);
    setFeedback([]);
  };

  const updateFeedbackStatus = async (id, status) => {
    const token = localStorage.getItem('cxsage_admin_token');
    try {
      await axios.patch(`${API}/admin/feedback/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(status === 'approved' ? 'Feedback published' : status === 'declined' ? 'Feedback declined' : 'Feedback updated');
      fetchData();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const deleteFeedback = async (id) => {
    const token = localStorage.getItem('cxsage_admin_token');
    try {
      await axios.delete(`${API}/admin/feedback/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Feedback deleted');
      fetchData();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const renderFeedbackGroup = (title, items, groupType) => {
    if (!items.length) return null;
    return (
      <div className="mb-8" data-testid={`feedback-group-${groupType}`}>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">{title} ({items.length})</h3>
        <div className="space-y-4">
          {items.map((f) => (
            <div key={f.id} className="border border-gray-200 p-4" data-testid={`feedback-item-${f.id}`}>
              <p className="text-gray-800 mb-3">"{f.feedback}"</p>
              <div className="text-sm text-gray-600 mb-3">
                <span className="font-medium text-gray-900">{f.name}</span>
                {` · ${[f.company, f.company_size, f.industry].filter(Boolean).join(' · ')}`}
              </div>
              <div className="flex flex-wrap gap-2">
                {groupType !== 'approved' && (
                  <button onClick={() => updateFeedbackStatus(f.id, 'approved')} data-testid={`approve-${f.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-[#00D4B4] text-white hover:bg-[#00BFA3]">
                    <Check className="w-4 h-4" /> Approve
                  </button>
                )}
                {groupType === 'new' && (
                  <button onClick={() => updateFeedbackStatus(f.id, 'declined')} data-testid={`decline-${f.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200">
                    <X className="w-4 h-4" /> Decline
                  </button>
                )}
                {groupType === 'approved' && (
                  <button onClick={() => updateFeedbackStatus(f.id, 'new')} data-testid={`unpublish-${f.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200">
                    <RotateCcw className="w-4 h-4" /> Unpublish
                  </button>
                )}
                {groupType === 'declined' && (
                  <button onClick={() => updateFeedbackStatus(f.id, 'new')} data-testid={`restore-${f.id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 hover:bg-gray-200">
                    <RotateCcw className="w-4 h-4" /> Restore
                  </button>
                )}
                <button onClick={() => deleteFeedback(f.id)} data-testid={`delete-${f.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-10 shadow-xl max-w-md w-full">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#00D4B4] flex items-center justify-center">
              <span className="text-white text-2xl font-bold">CX</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">CX Sage</h1>
              <p className="text-gray-600 text-sm">Admin Analytics</p>
            </div>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 text-gray-900 focus:outline-none focus:border-[#00D4B4]"
                placeholder="Enter admin username"
                autoComplete="username"
                data-testid="admin-username-input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Admin Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 text-gray-900 focus:outline-none focus:border-[#00D4B4]"
                placeholder="Enter admin password"
                autoComplete="current-password"
                data-testid="admin-password-input"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              className="w-full bg-[#00D4B4] text-white py-3 font-medium hover:bg-[#00BFA3] transition-colors disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }
  
  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading analytics...</div>
      </div>
    );
  }
  
  const deviceData = Object.entries(stats.device_types).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));
  
  const trafficData = Object.entries(stats.traffic_sources).map(([name, value]) => ({
    name,
    value
  }));
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white shadow-md p-6 mb-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-[#00D4B4] flex items-center justify-center">
              <span className="text-white text-2xl font-bold">CX</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
              <p className="text-gray-600 text-sm">CX Sage Website Metrics</p>
            </div>
          </div>
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-[#00D4B4] transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
        
        <div className="bg-white shadow-md p-4 mb-6">
          <div className="flex gap-2">
            {['daily', 'weekly', 'monthly', 'all'].map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-6 py-2 font-medium transition-colors ${
                  period === p
                    ? 'bg-[#00D4B4] text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {stats.recent_locations && stats.recent_locations.length > 0 && (
          <div className="bg-white shadow-md p-3 mb-6" data-testid="recent-locations-ticker">
            <div className="flex items-center gap-4">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#00D4B4] whitespace-nowrap flex items-center gap-1">
                <MapPin className="w-4 h-4" /> Recent
              </span>
              <div className="cx-ticker flex-1">
                <div className="cx-ticker-track">
                  {[...stats.recent_locations, ...stats.recent_locations].map((r, i) => (
                    <span key={i} className="cx-ticker-item">{r.label}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white p-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Eye className="w-6 h-6 text-blue-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.pageviews.toLocaleString()}</div>
            <div className="text-gray-600 text-sm">Total Pageviews</div>
          </div>
          
          <div className="bg-white p-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.unique_visitors.toLocaleString()}</div>
            <div className="text-gray-600 text-sm">Unique Visitors</div>
          </div>
          
          <div className="bg-white p-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <MousePointer className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.clicks.toLocaleString()}</div>
            <div className="text-gray-600 text-sm">Total Clicks</div>
          </div>
          
          <div className="bg-white p-6 shadow-md">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-1">{stats.avg_session_duration}s</div>
            <div className="text-gray-600 text-sm">Avg Session Duration</div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white p-6 shadow-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Traffic Over Time</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis dataKey="date" stroke="#4A4A4A" style={{ fontSize: '12px' }} />
                <YAxis stroke="#4A4A4A" />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
                <Legend />
                <Line type="monotone" dataKey="pageviews" stroke="#00D4B4" strokeWidth={2} name="Pageviews" />
                <Line type="monotone" dataKey="unique_visitors" stroke="#3B82F6" strokeWidth={2} name="Unique Visitors" />
                <Line type="monotone" dataKey="clicks" stroke="#8B5CF6" strokeWidth={2} name="Clicks" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          
          <div className="bg-white p-6 shadow-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Device Types</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={deviceData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {deviceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="bg-white p-6 shadow-md mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Traffic Sources</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={trafficData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
              <XAxis dataKey="name" stroke="#4A4A4A" />
              <YAxis stroke="#4A4A4A" />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }} />
              <Bar dataKey="value" fill="#00D4B4" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white p-6 shadow-md" data-testid="top-cities-card">
            <div className="flex items-center gap-3 mb-4">
              <MapPin className="w-6 h-6 text-[#00D4B4]" />
              <h2 className="text-xl font-bold text-gray-900">Top Cities</h2>
            </div>
            {stats.top_cities && stats.top_cities.length > 0 ? (
              <div className="space-y-3">
                {stats.top_cities.map((c, index) => {
                  const max = stats.top_cities[0].visitors || 1;
                  return (
                    <div key={index} data-testid={`top-city-${index}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-400 w-5">{index + 1}</span>
                          <span className="text-gray-900 font-medium">{c.city}</span>
                          {c.country && <span className="text-gray-500 text-sm">{c.country}</span>}
                        </div>
                        <span className="text-gray-600 text-sm whitespace-nowrap">{c.visitors} {c.visitors === 1 ? 'visitor' : 'visitors'}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#00D4B4] rounded-full" style={{ width: `${(c.visitors / max) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No city data yet — cities will appear here as traffic comes in.</p>
            )}
          </div>

          <div className="bg-white p-6 shadow-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Top Locations</h2>
            <div className="space-y-4">
              {stats.top_locations.slice(0, 5).map((location, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-[#00D4B4]" />
                    <span className="text-gray-900 font-medium">{location.location || location.country}</span>
                  </div>
                  <span className="text-gray-600">{location.visitors} visitors</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 shadow-md mb-6" data-testid="load-time-trend-card">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp className="w-6 h-6 text-[#00D4B4]" />
            <h2 className="text-xl font-bold text-gray-900">Page Load Speed Over Time</h2>
          </div>
          {timeline.some((t) => t.avg_load_time != null) ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={timeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
                <XAxis dataKey="date" stroke="#4A4A4A" style={{ fontSize: '12px' }} />
                <YAxis stroke="#4A4A4A" label={{ value: 'ms', angle: -90, position: 'insideLeft', fill: '#4A4A4A' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb' }}
                  formatter={(value) => [`${value} ms`, 'Avg Load Time']}
                />
                <Line type="monotone" dataKey="avg_load_time" stroke="#F59E0B" strokeWidth={2} name="Avg Load Time (ms)" connectNulls dot={{ fill: '#F59E0B', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-sm">No load-time data yet — this trend will populate as new pageviews come in.</p>
          )}
        </div>

        <div className="bg-white p-6 shadow-md mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Globe className="w-6 h-6 text-[#00D4B4]" />
            <h2 className="text-xl font-bold text-gray-900">Visitor Map</h2>
          </div>
          {stats.geo_points && stats.geo_points.length > 0 ? (
            <WorldMap points={stats.geo_points} />
          ) : (
            <p className="text-gray-500 text-sm">No location data yet — visitor locations will appear here as traffic comes in.</p>
          )}
        </div>

        <div className="bg-white p-6 shadow-md mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Top Pages</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-3 text-gray-900 font-semibold">Page URL</th>
                  <th className="text-right p-3 text-gray-900 font-semibold">Views</th>
                </tr>
              </thead>
              <tbody>
                {stats.top_pages.map((page, index) => (
                  <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3 text-gray-700">{page.url}</td>
                    <td className="p-3 text-right text-gray-900 font-medium">{page.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <TrendingUp className="w-6 h-6 text-[#00D4B4]" />
              <h2 className="text-xl font-bold text-gray-900">Page Load Speed</h2>
            </div>
            <div className="text-3xl font-bold text-gray-900 mb-2">{stats.avg_page_load_speed}ms</div>
            <div className="text-gray-600">Average page load time</div>
          </div>
          
          <div className="bg-white p-6 shadow-md">
            <div className="flex items-center gap-3 mb-4">
              <Smartphone className="w-6 h-6 text-[#00D4B4]" />
              <h2 className="text-xl font-bold text-gray-900">Mobile vs Desktop</h2>
            </div>
            <div className="space-y-2">
              {Object.entries(stats.device_types).map(([device, count]) => (
                <div key={device} className="flex justify-between items-center">
                  <span className="text-gray-700 capitalize">{device}</span>
                  <span className="text-gray-900 font-medium">{count} ({((count / stats.pageviews) * 100).toFixed(1)}%)</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Client Feedback Management */}
        <div className="bg-white p-6 shadow-md mt-6" data-testid="feedback-admin-section">
          <div className="flex items-center gap-3 mb-6">
            <MessageSquare className="w-6 h-6 text-[#00D4B4]" />
            <h2 className="text-xl font-bold text-gray-900">Client Feedback</h2>
          </div>
          {renderFeedbackGroup('Pending Review', feedback.filter((f) => (f.status || 'new') === 'new'), 'new')}
          {renderFeedbackGroup('Published', feedback.filter((f) => f.status === 'approved'), 'approved')}
          {renderFeedbackGroup('Declined', feedback.filter((f) => f.status === 'declined'), 'declined')}
          {feedback.length === 0 && <p className="text-gray-500">No feedback submissions yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default Admin;
